/**
 * FILE: logger.service.ts
 * CHỨC NĂNG: Service logger trung tâm, bật/tắt theo môi trường.
 *
 * - Dev: log đầy đủ ra console (bật = true).
 * - Production: chỉ SuperAdmin được xem log trên console.
 * - `console.error` luôn chạy kể cả production (quan trọng cho monitoring).
 *
 * Usage:
 * const logger = inject(LoggerService);
 * logger.log('Extension check:', result);
 * logger.warn('Cảnh báo...');
 * logger.error('Lỗi:', error);
 */
import {Injectable, isDevMode} from '@angular/core';
import {AuthService} from '@core/authentication/auth.service';

@Injectable({providedIn: 'root'})
export class LoggerService {
  constructor(private readonly authService: AuthService) {
  }

  /**
   * Dev luôn log. Production chỉ log cho SuperAdmin đã đăng nhập.
   * Getter kiểm tra role tại runtime, sau khi auth đã được hydrate.
   */
  get enabled(): boolean {
    return isDevMode() || this.authService.isSuperAdmin();
  }

  // ─── Methods chính ──────────────────────────────────────────────────────

  log(...args: any[]): void {
    if (this.enabled) {
      console.log(...args);
    }
  }

  warn(...args: any[]): void {
    if (this.enabled) {
      console.warn(...args);
    }
  }

  error(...args: any[]): void {
    // error luôn log — kể cả production — vì cần bắt lỗi quan trọng
    console.error(...args);
  }

  info(...args: any[]): void {
    if (this.enabled) {
      console.info(...args);
    }
  }

  debug(...args: any[]): void {
    if (this.enabled) {
      console.debug(...args);
    }
  }

  /**
   * Ghi log có nhãn — format: [LABEL] message
   */
  labeled(label: string, ...args: any[]): void {
    if (this.enabled) {
      console.log(`[${label}]`, ...args);
    }
  }

  /**
   * Ghi log theo nhóm — dùng console.group
   */
  group(label: string, ...args: any[]): void {
    if (this.enabled) {
      console.group(label);
      args.forEach(arg => console.log(arg));
      console.groupEnd();
    }
  }
}
